// Combo table of both visits and visit requests
import { IClinicianChip } from '@/components/clinicians/ClinicianChip';
import { IDisciplineChip } from '@/components/disciplines/DisciplineChip';
import { CaptionLabel } from '@/components/labels/CaptionLabel';
import { NoneLabel } from '@/components/labels/NoneLabel';
import { IOrderChip, OrderChip } from '@/components/orders/OrderChip';
import { getVisitSortDate } from '@/components/queues/helpers';
import { AxleTable } from '@/components/tables/AxleTable';
import { EditableTextCell } from '@/components/text/EditableTextCell';
import { OrderVisitColumns } from '@/components/unifiedVisits/columns';
import { WorkflowStepCountChip } from '@/components/unifiedVisits/components';
import { getWorkflowStepCounts } from '@/components/unifiedVisits/utils';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { PaginationByPage } from '@/components/utils/pagination';
import { VisitRequestDateTypeDisplay } from '@/components/visitRequests/VisitRequestDateTypeDisplay';
import { VisitRequestWorkflowStepChip } from '@/components/visitRequests/VisitRequestWorkflowStepChip';
import { IVisitRequestActionsCell } from '@/components/visitRequests/tables/VisitRequestActionsCell';
import {
    AppointmentClinicianCell,
    AppointmentDisciplineServicesCell,
    AppointmentServicesCell,
    VisitRequestPatientCell,
} from '@/components/visitRequests/tables/cells';
import { VisitRequestColumns } from '@/components/visitRequests/tables/columns';
import { VisitOutcomeChip, VisitStateChip } from '@/components/visits/StatusAndOutcomeChips';
import { VisitDateTypeDisplay } from '@/components/visits/VisitDateTypeDisplay';
import { Visit } from '@/components/visits/tables/VisitActionsCell';
import {
    ClinicianAppointmentClinicianCell,
    ClinicianAppointmentDisciplineServicesCell,
    ClinicianAppointmentServicesCell,
    VisitPatientCell,
} from '@/components/visits/tables/cells';
import { VisitColumns } from '@/components/visits/tables/columns';
import { LIGHT_AXLE_TEAL_BACKGROUND } from '@/constants/colors';
import { IOptionalDateWindow, IOptionalTimeWindow } from '@/types/datetimes';
import { FeatureFlag } from '@/types/featureFlag';
import {
    ClinicianAppointmentModality,
    SyncState,
    VisitRequestState,
    VisitRequestWorkflowStep,
    VisitWorkflowStep,
} from '@/types/graphql';
import {
    VisitOutcome,
    VisitPriority,
    VisitState,
    workflowStepFromVisitRequestWorkflowStep,
} from '@/types/visit';
import { IBasicAddressWithCoordinates } from '@/utils/addresses';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { Box, TableCell, TableRow } from '@mui/material';

import { OptionalDateFulfillmentWindowDisplay } from '../fulfillmentWindows/OptionalDateFulfillmentWindowDisplay';
import { VisitWorkflowStepChip } from '../visits/VisitWorkflowStepChip';

// Hoisted so each cell render reuses the same props/sx reference instead of
// forcing Emotion to re-serialize the style for every cell on every pass.
const STATE_CELL_PROPS = {
    sx: {
        maxWidth: '150px',
    },
};
const HIGHLIGHTED_WORKFLOW_STEP_CELL_PROPS = {
    sx: { backgroundColor: `${LIGHT_AXLE_TEAL_BACKGROUND}` },
};
const EMPTY_CELL_PROPS = {};

export const UnifiedVisitRowFragment = gql`
    fragment UnifiedVisitRow on Visit {
        __typename
        id
        effectivePriority
        visitWorkflowStep: workflowStep
        visitState: state
        outcome
        startDatetime
        tentativeDate
        visitTimezone: timezone
        patient {
            id
            fullName
            phoneNumber
            ...PatientTags
        }
        permissions {
            id
            reschedule
            assign
            cancel
            editState
            editOutcome
            editWithCompletedOutcome
        }
        clinicianAppointments {
            id
            clinician {
                fullName
                ...ClinicianChipFragment
                address {
                    ...BasicAddressWithCoordinatesFragment
                }
            }
            modality
            services {
                id
                name
                isActive
            }
        }
        visitFulfillmentWindow: fulfillmentWindow {
            startDatetime
            endDatetime
            asDateWindow {
                startDate
                endDate
            }
        }
        disciplines {
            ...DisciplineChipFragment
        }
        warnings {
            message
        }
        order {
            fulfillmentWindow {
                startDate
            }
            ...OrderChipFragment
            orderNotes
        }
        visitAddress: address {
            ...BasicAddressWithCoordinatesFragment
        }
        ...VisitActionsCellFragment
        syncStatus {
            id
            state
            lastAttempt {
                id
                createdAt
            }
            lastSuccess {
                id
                createdAt
            }
            lastFailure {
                id
                userSafeErrorMessage
            }
        }
    }
`;

export const UnifiedVisitRequestRowFragment = gql`
    fragment UnifiedVisitRequestRow on VisitRequest {
        __typename
        id
        priority: priority
        visitRequestState: state
        visitRequestWorkflowStep: workflowStep
        visitRequestFulfillmentWindow: fulfillmentWindow {
            startDate
            endDate
        }
        availableServices {
            id
            name
            isVirtual
        }
        visitRequestTimezone: timezone
        disciplines {
            ...DisciplineChipFragment
        }
        appointments {
            id
            services {
                id
                name
                isActive
            }
            clinician {
                fullName
                ...ClinicianChipFragment
            }
        }
        warnings {
            message
        }
        order {
            fulfillmentWindow {
                startDate
            }
            ...OrderChipFragment
            orderNotes
        }
        tentativeDate
        patient {
            id
            fullName
            ...PatientTags
        }
        syncStatus {
            id
            state
            lastAttempt {
                id
                createdAt
            }
            lastFailure {
                id
                userSafeErrorMessage
            }
        }
        visitRequestAddress: address {
            ...BasicAddressWithCoordinatesFragment
        }
        ...VisitRequestActionsCellFragment
    }
`;

// Graph types
export const UnifiedVisitOccurrenceFragments = gql`
    ${UnifiedVisitRowFragment}
    ${UnifiedVisitRequestRowFragment}
`;

// Union type of Visit and VisitRequest
export interface IUnifiedVisit extends Visit {
    __typename: 'Visit';
    id: string;
    effectivePriority: VisitPriority;
    visitWorkflowStep: VisitWorkflowStep;
    visitState: VisitState;
    outcome: VisitOutcome;
    requiresReschedule: boolean | null;
    visitFulfillmentWindow: IOptionalTimeWindow | null;
    startDatetime: Date | null;
    tentativeDate: Date | null;
    visitTimezone: string;
    disciplines: IDisciplineChip[];
    clinicianAppointments: {
        id: string;
        clinician: (IClinicianChip & { address: IBasicAddressWithCoordinates | null }) | null;
        modality: ClinicianAppointmentModality;
        services: {
            id: string;
            name: string;
        }[];
    }[];
    appointments: { id: string; services: { id: string; name: string }[] }[];
    order:
        | ({
              fulfillmentWindow: { startDate: string | null } | null;
              orderNotes: string;
          } & IOrderChip)
        | null;
    patient: {
        id: string;
        fullName: string;
        phoneNumber?: string | null;
    };
    visitAddress: IBasicAddressWithCoordinates | null;
    warnings: { message: string }[];
    permissions: {
        id: string;
        reschedule: boolean;
        assign: boolean;
        cancel: boolean;
        editState: boolean;
        editOutcome: boolean;
        editWithCompletedOutcome: boolean;
    };
    syncStatus: {
        id: string;
        state: SyncState;
        lastAttempt: {
            id: string;
            createdAt: Date;
        } | null;
        lastSuccess: {
            id: string;
            createdAt: Date;
        } | null;
        lastFailure: {
            id: string;
            userSafeErrorMessage: string | null;
        } | null;
    } | null;
}
export function isUnifiedVisit(row: UnifiedVisitOccurrence): row is IUnifiedVisit {
    return row.__typename === 'Visit';
}

export interface IUnifiedVisitRequest extends IVisitRequestActionsCell {
    __typename: 'VisitRequest';
    id: string;
    priority: VisitPriority;
    visitRequestState: VisitRequestState;
    visitRequestWorkflowStep: VisitRequestWorkflowStep;
    visitRequestFulfillmentWindow: IOptionalDateWindow | null;
    tentativeDate: Date | null;
    visitRequestTimezone: string | null;
    availableServices: {
        id: string;
        name: string;
        isVirtual: boolean;
    }[];
    disciplines: IDisciplineChip[];
    appointments: {
        id: string;
        services: {
            id: string;
            name: string;
        }[];
        clinician: IClinicianChip | null;
    }[];
    order:
        | ({
              fulfillmentWindow: { startDate: string | null } | null;
              orderNotes: string;
          } & IOrderChip)
        | null;
    warnings: { message: string }[];
    patient: {
        id: string;
        fullName: string;
    };
    visitRequestAddress: IBasicAddressWithCoordinates | null;
    syncStatus: {
        id: string;
        state: SyncState;
        lastAttempt: {
            id: string;
            createdAt: Date;
        } | null;
        lastFailure: {
            id: string;
            userSafeErrorMessage: string | null;
        } | null;
    } | null;
}

export function isUnifiedVisitRequest(row: UnifiedVisitOccurrence): row is IUnifiedVisitRequest {
    return row.__typename === 'VisitRequest';
}

// Union type of visits and visit requests, the input to this table.
export type UnifiedVisitOccurrence = IUnifiedVisit | IUnifiedVisitRequest;

// Row Item Types, consistencing of the graph types above and a few derived fields.
// for table display and sorting purposes.
interface IUnifiedVisitRowBase {
    sortPriority: number;
    sortStateIndex: number;
    displayPriority: VisitPriority;
    sortDate: Date | null;
    cliniciansSortKey: string;
    sortServicesName: string;
    link: string;
    orderSortKey: number;
}
export type UnifiedVisitOccurrenceRow = UnifiedVisitRow | UnifiedVisitRequestRow;
export type UnifiedVisitRow = IUnifiedVisitRowBase &
    IUnifiedVisit & { workflowStep: VisitWorkflowStep };
export type UnifiedVisitRequestRow = IUnifiedVisitRowBase &
    IUnifiedVisitRequest & { workflowStep: VisitWorkflowStep };
export function isVisitRow(row: UnifiedVisitOccurrenceRow): row is UnifiedVisitRow {
    return row.__typename === 'Visit';
}

export function isVisitRequestRow(row: UnifiedVisitOccurrenceRow): row is UnifiedVisitRequestRow {
    return row.__typename === 'VisitRequest';
}

// Conversion helpers from graph types to table row types
function unifiedVisitToUnifiedVisitRow(visit: IUnifiedVisit): UnifiedVisitRow {
    return {
        ...visit,
        // The Visit fragment doesn't query `appointments` (it would collide with
        // VisitRequest.appointments on union queries), but the shared bulk-services
        // selection hook reads `row.appointments` per-typename. Derive it from
        // clinicianAppointments here so the hook's seed array matches the rendered
        // appointments — without it, `selectedAppointmentServicesState` is empty
        // and per-appointment service edits silently no-op.
        appointments: visit.clinicianAppointments.map((appointment) => ({
            id: appointment.id,
            services: appointment.services,
        })),
        displayPriority: visit.effectivePriority,
        sortDate: getVisitSortDate({
            ...visit,
            fulfillmentWindow: visit.visitFulfillmentWindow,
        }),
        link: `/visits/${visit.id}`,
        cliniciansSortKey: VisitColumns.createCliniciansSortKey(visit.clinicianAppointments),
        sortPriority: VisitColumns.createPrioritySortIndex(visit.effectivePriority),
        sortServicesName: VisitColumns.createServicesSortKey(visit.clinicianAppointments),
        sortStateIndex: VisitColumns.createUnifiedStateIndex(visit.visitState),
        orderSortKey: OrderVisitColumns.createOrderSortKey(visit.order),
        workflowStep: visit.visitWorkflowStep,
    };
}

function unifiedVisitRequestToUnifiedVisitRequestRow(
    visitRequest: IUnifiedVisitRequest,
): UnifiedVisitRequestRow {
    return {
        ...visitRequest,
        sortStateIndex: VisitRequestColumns.createUnifiedStateIndex(
            visitRequest.visitRequestWorkflowStep,
        ),
        displayPriority: visitRequest.priority,
        sortDate: getVisitSortDate({
            ...visitRequest,
            fulfillmentWindow: visitRequest.visitRequestFulfillmentWindow,
        }),
        cliniciansSortKey: VisitColumns.createCliniciansSortKey(visitRequest.appointments),
        sortPriority: VisitColumns.createPrioritySortIndex(visitRequest.priority),
        sortServicesName: VisitColumns.createServicesSortKey(visitRequest.appointments),
        link: visitRequest.visit
            ? `/visits/${visitRequest.visit.id}`
            : `/visit-requests/${visitRequest.id}`,
        orderSortKey: OrderVisitColumns.createOrderSortKey(visitRequest.order),
        workflowStep: workflowStepFromVisitRequestWorkflowStep(
            visitRequest.visitRequestWorkflowStep,
        ),
    };
}

export function unifiedVisitOccurrenceToUnifiedVisitOccurrenceRow(
    occurrence: UnifiedVisitOccurrence,
): UnifiedVisitOccurrenceRow {
    if (isUnifiedVisit(occurrence)) {
        return unifiedVisitToUnifiedVisitRow(occurrence);
    }
    if (isUnifiedVisitRequest(occurrence)) {
        return unifiedVisitRequestToUnifiedVisitRequestRow(occurrence);
    }
    throw new Error('Unknown occurrence type');
}

export const UnifiedVisitsAndRequestsTable = ({
    visits,
    visitRequests,
    showWorkflowCounts = false,
    highlightWorkflowStepCells = false,
    title,
    showDisciplines,
    showOrder,
    showPatient,
    showPatientTags,
    numRows,
    pagination,
    selectableRows = 'none',
    isRowSelectable,
    selectedRowValues,
    setSelectedRowValues,
    stickyHeader,
    horizontalScrollButtons,
    pinActions,
}: {
    visits: IUnifiedVisit[];
    visitRequests: IUnifiedVisitRequest[];
    showWorkflowCounts?: boolean;
    highlightWorkflowStepCells?: boolean;
    title?: string;
    showOrder?: boolean;
    showDisciplines?: boolean;
    showPatient?: boolean;
    // Renders the patient's team-tag chips in the Patient column. Off by default so this
    // shared table only shows them where a caller opts in (the All Visits table).
    showPatientTags?: boolean;
    numRows: number;
    pagination?: PaginationByPage;
    selectableRows?: 'none' | 'multiple';
    isRowSelectable?: (row: UnifiedVisitOccurrenceRow) => boolean;
    selectedRowValues?: string[];
    setSelectedRowValues?: (values: string[]) => void;
    stickyHeader?: boolean;
    horizontalScrollButtons?: boolean;
    pinActions?: boolean;
}) => {
    const occurrences = [...visits, ...visitRequests];
    return (
        <UnifiedVisitOccurrencesTable
            occurrences={occurrences}
            showWorkflowCounts={showWorkflowCounts}
            highlightWorkflowStepCells={highlightWorkflowStepCells}
            title={title}
            showDisciplines={showDisciplines}
            showOrder={showOrder}
            showPatient={showPatient}
            showPatientTags={showPatientTags}
            numRows={numRows}
            pagination={pagination}
            selectableRows={selectableRows}
            isRowSelectable={isRowSelectable}
            selectedRowValues={selectedRowValues}
            setSelectedRowValues={setSelectedRowValues}
            stickyHeader={stickyHeader}
            horizontalScrollButtons={horizontalScrollButtons}
            pinActions={pinActions}
        />
    );
};

/**
 * Table Components
 */
export const UnifiedVisitOccurrencesTable = ({
    occurrences,
    showWorkflowCounts = false,
    highlightWorkflowStepCells = false,
    title,
    numRows,
    showOrder = true,
    showPatient = true,
    showPatientTags = false,
    showDisciplines = true,
    pagination,
    selectableRows = 'none',
    isRowSelectable,
    selectedRowValues,
    setSelectedRowValues,
    stickyHeader,
    horizontalScrollButtons,
    pinActions = false,
}: {
    occurrences: UnifiedVisitOccurrence[];
    highlightWorkflowStepCells?: boolean;
    showOrder?: boolean;
    showDisciplines?: boolean;
    showPatient?: boolean;
    showPatientTags?: boolean;
    showWorkflowCounts?: boolean;
    numRows: number;
    title?: string;
    pagination?: PaginationByPage;
    selectableRows?: 'none' | 'multiple';
    isRowSelectable?: (row: UnifiedVisitOccurrenceRow) => boolean;
    selectedRowValues?: string[];
    setSelectedRowValues?: (values: string[]) => void;
    stickyHeader?: boolean;
    horizontalScrollButtons?: boolean;
    pinActions?: boolean;
}) => {
    // Order columns are gated behind the Orders feature flag.
    // Callers can also force them off via showOrder (eg the order detail tables, where they're redundant).
    const hasOrders = useFeatureFlagEnabled(FeatureFlag.ORDERS);
    const showOrderColumns = showOrder && hasOrders;

    // Transformed data
    const occurrenceRows: UnifiedVisitOccurrenceRow[] = occurrences.map((occurrence) => {
        return unifiedVisitOccurrenceToUnifiedVisitOccurrenceRow(occurrence);
    });
    const workflowStepCounts = showWorkflowCounts
        ? getWorkflowStepCounts({
              occurrences: occurrenceRows,
          })
        : {};

    function createWorkflowStepSetCellProps(workflowStep: VisitWorkflowStep) {
        return (_: string, rowIndex: number) => {
            if (highlightWorkflowStepCells) {
                const row = occurrenceRows[rowIndex];
                if (isVisitRow(row)) {
                    return row.visitWorkflowStep === workflowStep
                        ? HIGHLIGHTED_WORKFLOW_STEP_CELL_PROPS
                        : EMPTY_CELL_PROPS;
                }
                if (isVisitRequestRow(row)) {
                    return workflowStepFromVisitRequestWorkflowStep(
                        row.visitRequestWorkflowStep,
                    ) === workflowStep
                        ? HIGHLIGHTED_WORKFLOW_STEP_CELL_PROPS
                        : EMPTY_CELL_PROPS;
                }
            }
            return EMPTY_CELL_PROPS;
        };
    }

    return (
        <AxleTable
            title={title}
            data={occurrenceRows}
            elevation={0}
            toolbar={false}
            stickyHeader={stickyHeader}
            horizontalScrollButtons={horizontalScrollButtons}
            dynamicPaging={pagination ? { ...pagination.dynamicPaging, numRows } : undefined}
            extraOptions={{
                pagination: pagination !== undefined,
                sortOrder: {
                    name: 'sortDate',
                    direction: 'asc',
                },
                // Only render empty footer for padding when pagination is not enabled.
                // When pagination IS enabled, AxleTable renders its own pagination footer.
                ...(pagination === undefined && {
                    customFooter: () => (
                        <tfoot>
                            <TableRow>
                                <TableCell sx={{ border: 'none' }} />
                            </TableRow>
                        </tfoot>
                    ),
                }),
            }}
            selectableRows={selectableRows}
            rowToSelectionIdentifier={
                selectableRows !== 'none' ? (row: UnifiedVisitOccurrenceRow) => row.id : undefined
            }
            isRowSelectable={selectableRows !== 'none' ? isRowSelectable : undefined}
            selectedRowValues={selectableRows !== 'none' ? selectedRowValues : undefined}
            setSelectedRowValues={selectableRows !== 'none' ? setSelectedRowValues : undefined}
            rowToLink={(row: UnifiedVisitOccurrenceRow) => row.link}
            columns={[
                OrderVisitColumns.priorityColumn,
                {
                    field: 'sortStateIndex',
                    label: 'State',
                    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
                        if (isVisitRow(row)) {
                            return (
                                <VerticalStack spacing={1} justifyContent='center'>
                                    {row.outcome === VisitOutcome.NONE && (
                                        <VisitWorkflowStepChip
                                            workflowStep={row.visitWorkflowStep}
                                            onlyPending={true}
                                        />
                                    )}
                                    <VisitStateChip state={row.visitState} />
                                    {row.outcome !== VisitOutcome.NONE && (
                                        <VisitOutcomeChip outcome={row.outcome} />
                                    )}
                                </VerticalStack>
                            );
                        }
                        if (isVisitRequestRow(row)) {
                            return (
                                <VerticalStack spacing={1} justifyContent='center'>
                                    <VisitRequestWorkflowStepChip
                                        workflowStep={row.visitRequestWorkflowStep}
                                    />
                                </VerticalStack>
                            );
                        }
                        return <NoneLabel />;
                    },
                    options: {
                        customHeadLabelRender: () => {
                            return (
                                <HorizontalStack width='auto' spacing={1}>
                                    <Box>State</Box>
                                    {showWorkflowCounts && (
                                        <WorkflowStepCountChip
                                            count={
                                                workflowStepCounts[
                                                    VisitRequestWorkflowStep.READY_FOR_PROMOTION
                                                ]
                                            }
                                        />
                                    )}
                                </HorizontalStack>
                            );
                        },
                        setCellProps: () => STATE_CELL_PROPS,
                        sort: true,
                    },
                },
                {
                    field: 'patient.fullName',
                    label: 'Patient',
                    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
                        if (isVisitRow(row)) {
                            return (
                                <VisitPatientCell
                                    visit={{ ...row, address: row.visitAddress }}
                                    showTags={showPatientTags}
                                />
                            );
                        }
                        if (isVisitRequestRow(row)) {
                            return (
                                <VisitRequestPatientCell
                                    visitRequest={{ ...row, address: row.visitRequestAddress }}
                                    showTags={showPatientTags}
                                />
                            );
                        }
                        return <NoneLabel />;
                    },
                    hidden: !showPatient,
                },
                {
                    field: 'sortServicesName',
                    label: 'Services',
                    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
                        if (isVisitRow(row)) {
                            return showDisciplines ? (
                                <ClinicianAppointmentDisciplineServicesCell {...row} />
                            ) : (
                                <ClinicianAppointmentServicesCell {...row} />
                            );
                        }
                        if (isVisitRequestRow(row)) {
                            return showDisciplines ? (
                                <AppointmentDisciplineServicesCell {...row} />
                            ) : (
                                <AppointmentServicesCell {...row} />
                            );
                        }
                        return <NoneLabel />;
                    },
                    options: {
                        setCellProps: createWorkflowStepSetCellProps(
                            VisitWorkflowStep.NEEDS_SERVICE,
                        ),
                        customHeadLabelRender: () => {
                            return (
                                <HorizontalStack width='auto' spacing={1}>
                                    <Box>Services</Box>
                                    {showWorkflowCounts && (
                                        <WorkflowStepCountChip
                                            count={
                                                workflowStepCounts[
                                                    VisitRequestWorkflowStep.NEEDS_SERVICES
                                                ]
                                            }
                                        />
                                    )}
                                </HorizontalStack>
                            );
                        },
                    },
                },
                {
                    field: 'cliniciansSortKey',
                    label: 'Clinician',
                    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
                        if (isVisitRow(row)) {
                            return (
                                <>
                                    {/* Empty label aligns the clinician with the services */}
                                    {showDisciplines && <CaptionLabel>&nbsp;</CaptionLabel>}
                                    <ClinicianAppointmentClinicianCell {...row} />
                                </>
                            );
                        }
                        if (isVisitRequestRow(row)) {
                            return (
                                <>
                                    {/* Empty label aligns the clinician with the services */}
                                    {showDisciplines && <CaptionLabel>&nbsp;</CaptionLabel>}
                                    <AppointmentClinicianCell {...row} />
                                </>
                            );
                        }
                        return <NoneLabel />;
                    },
                    options: {
                        setCellProps: createWorkflowStepSetCellProps(
                            VisitWorkflowStep.NEEDS_CLINICIAN,
                        ),
                        customHeadLabelRender: () => {
                            return (
                                <HorizontalStack width='auto' spacing={1}>
                                    <Box>Clinician</Box>
                                    {showWorkflowCounts && (
                                        <WorkflowStepCountChip
                                            count={
                                                workflowStepCounts[
                                                    VisitRequestWorkflowStep.NEEDS_CLINICIAN
                                                ]
                                            }
                                        />
                                    )}
                                </HorizontalStack>
                            );
                        },
                    },
                },
                {
                    field: 'sortDate',
                    label: 'Date',
                    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
                        if (isVisitRow(row)) {
                            return (
                                <VisitDateTypeDisplay
                                    {...row}
                                    effectiveDate={null}
                                    fulfillmentWindow={row.visitFulfillmentWindow}
                                    timezone={row.visitTimezone}
                                    fallbackDate={null}
                                    label={false}
                                    datetimeProps={{ fontSize: '14px' }}
                                    displayFulfillmentWindow={false}
                                />
                            );
                        }
                        if (isVisitRequestRow(row)) {
                            return (
                                <VisitRequestDateTypeDisplay
                                    {...row}
                                    fulfillmentWindow={row.visitRequestFulfillmentWindow}
                                    label={false}
                                    dateProps={{ fontSize: '14px' }}
                                />
                            );
                        }
                        return <NoneLabel />;
                    },
                    options: {
                        setCellProps: createWorkflowStepSetCellProps(VisitWorkflowStep.NEEDS_DATE),
                        customHeadLabelRender: () => {
                            return (
                                <HorizontalStack width='auto' spacing={1}>
                                    <Box>Date</Box>
                                    {showWorkflowCounts && (
                                        <WorkflowStepCountChip
                                            count={
                                                workflowStepCounts[
                                                    VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE
                                                ]
                                            }
                                        />
                                    )}
                                </HorizontalStack>
                            );
                        },
                    },
                },
                {
                    field: 'fulfillmentWindow.startDate',
                    label: 'Fulfillment Window',
                    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
                        if (isUnifiedVisit(row) && row.visitFulfillmentWindow?.asDateWindow) {
                            return (
                                <OptionalDateFulfillmentWindowDisplay
                                    fulfillmentWindow={row.visitFulfillmentWindow.asDateWindow}
                                    label={false}
                                />
                            );
                        }
                        if (isUnifiedVisitRequest(row) && row.visitRequestFulfillmentWindow) {
                            return (
                                <OptionalDateFulfillmentWindowDisplay
                                    fulfillmentWindow={row.visitRequestFulfillmentWindow}
                                    label={false}
                                />
                            );
                        }
                        return <NoneLabel fontSize={'14px'} />;
                    },
                },
                {
                    field: 'orderSortKey',
                    label: 'Order',
                    rowToComponent: (row: UnifiedVisitOccurrenceRow) =>
                        row.order ? (
                            // Let the order-frequency chip wrap within a bounded width
                            // instead of clipping long labels like "2x weekly for 12 weeks".
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    '& .MuiChip-root': { height: 'auto', maxWidth: '160px' },
                                    '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        paddingY: '3px',
                                    },
                                }}
                            >
                                <OrderChip order={row.order} />
                            </Box>
                        ) : (
                            <NoneLabel fontSize={'14px'} />
                        ),
                    hidden: !showOrderColumns,
                },
                {
                    field: 'order.orderNotes',
                    label: 'Order Notes',
                    rowToComponent: (row: UnifiedVisitOccurrenceRow) => (
                        <Box sx={{ fontSize: '14px' }}>
                            <EditableTextCell
                                text={row.order?.orderNotes ?? null}
                                title='Order Notes'
                            />
                        </Box>
                    ),
                    hidden: !showOrderColumns,
                },
                { ...OrderVisitColumns.actionsColumn, pinRight: pinActions },
            ]}
        />
    );
};
